/**
 * Ink U — the training portal at university.scada.ink — and the certification
 * and partner programmes built on it.
 *
 * Course titles mirror training/02-curriculum-index.md. The certification
 * tracks, exam rules and partner discount tiers are programme decisions: edit
 * them here and every page that mentions them follows.
 */

import { PARTNER_TIERS } from '@scada-ink/shared/cloud';

export interface Course {
  number: number;
  title: string;
  track: 'associate' | 'professional';
}

export const COURSES: Course[] = [
  { number: 1, title: 'Scada.Ink Overview', track: 'associate' },
  { number: 2, title: 'Getting Started', track: 'associate' },
  { number: 3, title: 'Devices & Connectivity', track: 'associate' },
  { number: 4, title: 'Tags', track: 'associate' },
  { number: 5, title: 'Historian & Data Infrastructure', track: 'associate' },
  { number: 6, title: 'Alarming', track: 'associate' },
  { number: 7, title: 'Dashboards', track: 'associate' },
  { number: 8, title: 'Symbols & Graphics', track: 'associate' },
  { number: 9, title: 'Custom Pages', track: 'associate' },
  { number: 10, title: 'Scripting', track: 'associate' },
  { number: 11, title: 'Workflows', track: 'associate' },
  { number: 12, title: 'Ladder Logic', track: 'associate' },
  { number: 13, title: 'Sequential Function Charts', track: 'professional' },
  { number: 14, title: 'Data Tables, SQL & Remote Databases', track: 'professional' },
  { number: 15, title: 'Reporting', track: 'professional' },
  { number: 16, title: 'SPC & Quality', track: 'professional' },
  { number: 17, title: 'OEE, Downtime & Shifts', track: 'professional' },
  { number: 18, title: 'MES: Work Orders, Recipes, Instructions', track: 'professional' },
  { number: 19, title: 'Andon, Kiosk & TV Displays', track: 'professional' },
  { number: 20, title: 'Security & User Management', track: 'professional' },
  { number: 21, title: 'Outbound Connectivity', track: 'professional' },
  { number: 22, title: 'Smart Assist', track: 'professional' },
  { number: 23, title: 'Appliance Administration', track: 'professional' },
  { number: 24, title: 'Maintenance, Backup & Troubleshooting', track: 'professional' },
  { number: 25, title: 'Machine Vision', track: 'professional' },
];

export const courseTitle = (n: number) => COURSES.find((c) => c.number === n)?.title ?? `Course ${n}`;

export interface Certification {
  id: 'associate' | 'professional';
  code: string;
  name: string;
  audience: string;
  summary: string;
  requirements: string[];
  skills: string[];
}

export const CERTIFICATIONS: Certification[] = [
  {
    id: 'associate',
    code: 'SCA',
    name: 'Scada.Ink Certified Associate',
    audience: 'Controls technicians, maintenance engineers and new integrators',
    summary:
      'Proves you can commission an appliance end to end: connect devices, model tags, historize, alarm, and build the dashboards and logic a line needs.',
    requirements: [
      'Complete Courses 1–12 on Ink U',
      'Pass every section quiz in those courses with 80% or better',
      'Pass the proctored Associate exam (60 questions, 90 minutes, 75% to pass)',
    ],
    skills: ['Device connectivity & tag modeling', 'Historian & alarming', 'Dashboards, symbols & custom pages', 'Scripting, workflows & ladder logic'],
  },
  {
    id: 'professional',
    code: 'SCP',
    name: 'Scada.Ink Certified Professional',
    audience: 'System integrators, solution architects and plant IT/OT leads',
    summary:
      'Proves you can design and support a production deployment: analytics, MES, security, outbound integration and the running of a fleet of appliances.',
    requirements: [
      'Hold the Scada.Ink Certified Associate credential',
      'Complete Courses 13–25 on Ink U',
      'Pass every section quiz in those courses with 80% or better',
      'Pass the proctored Professional exam, including a hands-on sandbox scenario',
    ],
    skills: ['Reporting, SPC & OEE', 'Work orders, recipes & instructions', 'Security, HTTPS & user management', 'OPC-UA, Sparkplug B & remote databases'],
  },
];

/** How long a certification stays current before a recertification exam. */
export const CERT_VALID_YEARS = 2;

/** Partner tiers live in the shared package so the staff console, the cloud and this page agree. */
export { PARTNER_TIERS };

export const MAX_PARTNER_DISCOUNT = Math.max(...PARTNER_TIERS.map((t) => t.discountPct));
